import * as React from 'react'
import { HTMLInputTypeAttribute } from 'react'
import { VariantProps } from 'class-variance-authority'
import Label from '@/components/ui/Label'
import { inputStyles } from '@/components/ui/Input'
import FormError from '@/components/ui/Forms/FormError'
import { FieldErrors } from 'react-hook-form'
import { cn } from '@/lib/classMerge'
import { Eye, EyeOff } from 'lucide-react'

export interface IFormInputProps extends VariantProps<typeof inputStyles> {
  type: HTMLInputTypeAttribute
  placeholder: string
  id: string
  label?: string
  wrapperClassName?: string
  className?: string
  labelClassName?: string
  icon?: React.ElementType
  iconClassName?: string
  error?: string | FieldErrors<FormData>
  disabled?: boolean
  autoComplete?: boolean
  errorWrapperClassName?: string
  isDummy?: boolean
}

const FormInput = React.forwardRef<HTMLInputElement, IFormInputProps>(
  (
    {
      placeholder,
      label,
      wrapperClassName,
      labelClassName,
      id,
      className,
      variant,
      icon: Icon,
      iconClassName,
      error,
      disabled = false,
      autoComplete = true,
      errorWrapperClassName,
      isDummy = false,
      ...props
    },
    ref
  ) => {
    const [isFieldVisible, setFieldVisibility] = React.useState<boolean>(false)

    const isPasswordInput = props.type === 'password'

    const toggleFieldVisibility = () => {
      if (isPasswordInput) {
        setFieldVisibility(!isFieldVisible)
      }
    }

    return (
      <div className={cn('relative', wrapperClassName)}>
        {!!label && <Label text={label} className={labelClassName} />}
        {!isDummy ? (
          <input
            id={id}
            ref={ref}
            placeholder={placeholder}
            className={cn(
              inputStyles({ variant }),
              'w-full border-[#d3d6da] focus:border-[#b8bbc0] focus:ring-[#d3d6da]/30',
              className,
              disabled && 'cursor-not-allowed opacity-50',
              !!Icon && 'pr-12'
            )}
            autoComplete={autoComplete ? 'on' : 'off'}
            disabled={disabled}
            {...props}
            type={
              isPasswordInput
                ? isFieldVisible
                  ? 'text'
                  : 'password'
                : props.type
            }
          />
        ) : (
          <div
            className={cn(
              inputStyles({ variant }),
              'w-full border-[#d3d6da] focus:border-[#b8bbc0] focus:ring-[#d3d6da]/30',
              className,
              'cursor-not-allowed opacity-50'
            )}
          >
            {placeholder}
          </div>
        )}
        {Icon && (
          <>
            {isPasswordInput ? (
              isFieldVisible ? (
                <EyeOff
                  onClick={toggleFieldVisibility}
                  className={cn(
                    iconClassName
                      ? iconClassName
                      : 'absolute right-4 top-[43px] text-ui-gray cursor-pointer'
                  )}
                />
              ) : (
                <Eye
                  onClick={toggleFieldVisibility}
                  className={cn(
                    iconClassName
                      ? iconClassName
                      : 'absolute right-4 top-[43px] text-ui-gray cursor-pointer'
                  )}
                />
              )
            ) : (
              <Icon
                onClick={toggleFieldVisibility}
                className={cn(
                  iconClassName
                    ? iconClassName
                    : 'absolute right-4 top-[43px] text-ui-gray',
                  isPasswordInput && 'cursor-pointer'
                )}
              />
            )}
          </>
        )}
        {!!error && (
          <FormError
            wrapperClassName={`mt-2 ${errorWrapperClassName || ''}`}
            text={error}
          />
        )}
      </div>
    )
  }
)

FormInput.displayName = 'FormInput'

export default FormInput
